// Saved-jobs view for the Jobs page's Saved tab.
import { useMemo, useRef, useState } from "react";
import styled, { css } from "styled-components";
import { FiBookmark, FiBriefcase, FiSearch } from "react-icons/fi";
import {
  JobGridCard,
  JobCardSkeleton,
  JobRowCard,
  JobRowSkeleton,
  JobRows,
  JobLayoutToggle,
  useJobLayout,
} from "./Jobs";
import Skeleton, { useFillCount } from "../components/Skeleton";
import { Btn, jobIdOf, firstCompany } from "../components/JobDetailView";

/* ─────────────────────────────────────────────────────────
   Toolbar (search + counter)
───────────────────────────────────────────────────────── */
const BmToolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 18px;
  flex-wrap: wrap;
`;
const Search = styled.div`
  flex: 1;
  max-width: 360px;
  position: relative;
  display: flex;
  align-items: center;
  /* flex-basis 0 lets the search squeeze to a sliver beside the seg tabs; give it its own line. */
  @media (max-width: 450px) {
    flex: 1 1 100%;
    max-width: none;
  }
  svg.lead {
    position: absolute;
    left: 11px;
    color: var(--ink-muted);
    pointer-events: none;
  }
  input {
    width: 100%;
    height: 34px;
    padding: 0 12px 0 36px;
    border-radius: 8px;
    border: 1px solid var(--line);
    background: var(--surface-tint);
    color: var(--ink);
    font: inherit;
    font-size: 13px;
    transition: border-color 0.12s, background 0.12s, box-shadow 0.12s;
    outline: 0;
  }
  input::placeholder {
    color: var(--ink-muted);
  }
  input:focus {
    background: var(--surface);
    border-color: var(--ink-3);
    box-shadow: 0 0 0 3px var(--ink-ring);
  }
`;
const BmCount = styled.div`
  font-size: 13px;
  color: var(--ink-muted);
  font-variant-numeric: tabular-nums;
  b {
    color: var(--ink);
    font-weight: 600;
  }
`;
// Open/closed filter inside the saved-jobs pane. Deliberately a lighter
// segmented control — the dark pill Tabs above it switch jobs vs companies.
const SegTabs = styled.div`
  display: inline-flex;
  border: 1px solid var(--line);
  border-radius: 8px;
  background: var(--surface-tint);
  padding: 2px;
  gap: 2px;
`;
const SegTab = styled.button`
  height: 28px;
  padding: 0 12px;
  border: 0;
  background: transparent;
  color: var(--ink-soft);
  border-radius: 6px;
  font: inherit;
  font-size: 12.5px;
  font-weight: 500;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
  transition: background 0.12s, color 0.12s;
  &:hover {
    color: var(--ink);
  }
  ${({ $active }) =>
    $active &&
    css`
      background: var(--surface);
      color: var(--ink);
      font-weight: 600;
      box-shadow: var(--shadow-sm);
    `}
  @media (max-width: 450px) {
    min-height: 0;
    height: 32px;
  }
`;
const SegCount = styled.span`
  font-size: 11px;
  font-variant-numeric: tabular-nums;
  opacity: 0.65;
`;
// Empty state for a filter tab that matched nothing (the pane itself has jobs).
const SJEmptyTab = styled.div`
  padding: 44px 20px;
  text-align: center;
  color: var(--ink-muted);
  font-size: 13.5px;
  border: 1px dashed var(--line);
  border-radius: 14px;
  b {
    display: block;
    color: var(--ink);
    font-size: 14.5px;
    font-weight: 600;
    margin-bottom: 5px;
  }
`;

const Pane = styled.div`
  padding: 24px 32px 60px;
  max-width: 1280px;
  margin: 0 auto;
  @media (max-width: 768px) {
    padding: 16px 18px 40px;
  }
`;

const SJGrid = styled.div`
  display: grid;
  /* min() so a 340px track can't overflow a 360–375px phone. */
  grid-template-columns: repeat(auto-fill, minmax(min(340px, 100%), 1fr));
  gap: 14px;
`;
/* ─────────────────────────────────────────────────────────
   Empty state
───────────────────────────────────────────────────────── */
const Empty = styled.div`
  display: grid;
  place-items: center;
  text-align: center;
  padding: 80px 20px;
  min-height: 60vh;
  /* Accent-tinted tile — matches the Dashboard empty state. */
  .art {
    width: 96px;
    height: 96px;
    border-radius: 24px;
    background: linear-gradient(155deg, var(--accent-soft), var(--surface-tint));
    border: 1px solid var(--line);
    color: var(--accent);
    display: grid;
    place-items: center;
    margin: 0 auto 14px;
    box-shadow: var(--shadow-md);
    box-shadow: 0 8px 22px -10px color-mix(in srgb, var(--accent) 35%, transparent);
  }
  h3 {
    font-family: "Instrument Serif", Georgia, serif;
    font-size: 26px;
    margin: 0 0 6px;
    color: var(--ink);
    font-weight: 400;
  }
  p {
    color: var(--ink-muted);
    max-width: 380px;
    margin: 0 auto 14px;
    font-size: 13.5px;
    line-height: 1.5;
  }
`;

// Shaped like the real list in the saved layout, enough rows to fill the screen.
export function SavedJobsSkeleton() {
  const [layout] = useJobLayout();
  const List = layout === "rows" ? JobRows : SJGrid;
  const Item = layout === "rows" ? JobRowSkeleton : JobCardSkeleton;
  const ref = useRef(null);
  const count = useFillCount(ref, { min: 6 });
  return (
    <Pane>
      <BmToolbar>
        <Search>
          <FiSearch className="lead" size={14} />
          <input disabled placeholder="Search saved jobs…" />
        </Search>
        <div style={{ flex: 1 }} />
        <Skeleton width={90} height={14} />
      </BmToolbar>
      <List ref={ref}>
        {Array.from({ length: count }).map((_, i) => (
          <Item key={i} />
        ))}
      </List>
    </Pane>
  );
}

const SAVED_TABS = [
  { key: "all", label: "All jobs" },
  { key: "open", label: "Open jobs" },
  { key: "closed", label: "Closed jobs" },
];

export function SavedJobsView({ jobs, onUnsave, onOpen }) {
  const [query, setQuery] = useState("");
  const [tab, setTab] = useState("all");
  const [layout, setLayout] = useJobLayout();

  // The server stamps `closed` on a bookmark whose posting is down.
  const counts = useMemo(() => {
    const closed = jobs.filter((j) => j.closed).length;
    return { all: jobs.length, open: jobs.length - closed, closed };
  }, [jobs]);

  const filtered = useMemo(() => {
    let list = jobs;
    if (tab === "open") list = list.filter((j) => !j.closed);
    else if (tab === "closed") list = list.filter((j) => j.closed);
    if (!query) return list;
    const q = query.toLowerCase();
    return list.filter((j) => {
      const title = (j.title || j.job_title || "").toLowerCase();
      const company = firstCompany(j.source).toLowerCase();
      return title.includes(q) || company.includes(q);
    });
  }, [jobs, query, tab]);

  // Keyed off resolved jobs: a bookmark that can't be loaded at all reads as "no saved jobs".
  if (jobs.length === 0) {
    return (
      <Empty>
        <div>
          <div className="art">
            <FiBookmark size={48} strokeWidth={1.3} />
          </div>
          <h3>No saved jobs yet</h3>
          <p>
            Star a job from your job board to pin it here. We'll keep it
            handy for sourcing later.
          </p>
          <Btn $primary as="a" href="/jobs">
            <FiBriefcase size={14} /> Browse jobs
          </Btn>
        </div>
      </Empty>
    );
  }

  return (
    <Pane>
      <BmToolbar>
        <SegTabs role="tablist">
          {SAVED_TABS.map((t) => (
            <SegTab
              key={t.key}
              type="button"
              role="tab"
              aria-selected={tab === t.key}
              $active={tab === t.key}
              onClick={() => setTab(t.key)}
            >
              {t.label}
              <SegCount>{counts[t.key]}</SegCount>
            </SegTab>
          ))}
        </SegTabs>
        <Search>
          <FiSearch className="lead" size={14} />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search saved jobs…"
          />
        </Search>
        <div style={{ flex: 1 }} />
        <BmCount>
          <b>{filtered.length}</b> of {counts[tab]}{" "}
          {counts[tab] === 1 ? "job" : "jobs"}
        </BmCount>
        <JobLayoutToggle value={layout} onChange={setLayout} />
      </BmToolbar>
      {filtered.length === 0 ? (
        <SJEmptyTab>
          <b>
            {query
              ? "No matches"
              : tab === "closed"
                ? "No closed jobs"
                : "No open jobs"}
          </b>
          {query
            ? "Try a different title or company."
            : tab === "closed"
              ? "None of your saved jobs have closed yet."
              : "Every job you've saved has since closed."}
        </SJEmptyTab>
      ) : (
        (() => {
          const List = layout === "rows" ? JobRows : SJGrid;
          const Item = layout === "rows" ? JobRowCard : JobGridCard;
          return (
            <List>
              {filtered.map((j) => (
                <Item
                  key={jobIdOf(j)}
                  job={j}
                  saved
                  onOpen={onOpen}
                  onViewDetails={onOpen}
                  onToggleSave={onUnsave}
                />
              ))}
            </List>
          );
        })()
      )}
    </Pane>
  );
}
